<template>
    <div
        class="min-h-screen bg-slate-300 transition-all flex flex-col relative"
        :class="{
            'justify-center': true,
        }"
        :style="sectionBgColor ? { backgroundColor: sectionBgColor } : {}"
    >
        <!-- Hidden file input for background change -->
        <input
            v-if="isOwner"
            ref="bgInput"
            type="file"
            class="hidden"
            accept="image/*"
            @change="onBackgroundChange"
        />

        <!-- Hidden file input for user photo (mask mode) -->
        <input
            v-if="isOwner"
            ref="photoInput"
            type="file"
            class="hidden"
            accept="image/*"
            @change="onUserPhotoChange"
        />

        <!-- Floating save button (top-right) -->
        <button
            v-if="isOwner"
            class="fab-save-btn"
            :class="{ 'fab-save-btn--dirty': hasUnsavedChanges }"
            @click.stop="emitSave"
        >
            <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" />
            <span>Salveaza</span>
        </button>

        <!-- Floating Action Button (+) -->
        <transition name="fab-pop">
            <button
                v-if="isOwner && !selectedElement && !photoSelected && !selectedStickerId"
                class="fab-add"
                @click.stop="actionSheetOpen = !actionSheetOpen"
            >
                <font-awesome-icon
                    icon="fa-solid fa-plus"
                    :class="{ 'fab-add__icon--rotated': actionSheetOpen }"
                    class="fab-add__icon"
                />
            </button>
        </transition>

        <!-- Action sheet overlay -->
        <transition name="action-sheet-fade">
            <div
                v-if="actionSheetOpen"
                class="action-sheet-overlay"
                @click.stop="actionSheetOpen = false"
            ></div>
        </transition>

        <!-- Action sheet panel -->
        <transition name="action-sheet-slide">
            <div
                v-if="actionSheetOpen"
                class="action-sheet"
                @click.stop
            >
                <div class="action-sheet__header">
                    <span class="action-sheet__title">Adauga element</span>
                    <button class="action-sheet__close" @click.stop="actionSheetOpen = false">
                        <font-awesome-icon icon="fa-solid fa-xmark" />
                    </button>
                </div>

                <button class="action-sheet__item" @click.stop="actionSheetOpen = false; $nextTick(() => addText())">
                    <font-awesome-icon icon="fa-solid fa-i-cursor" class="action-sheet__icon" />
                    <span>Adauga text</span>
                </button>

                <button class="action-sheet__item" @click.stop="openBackgroundFile(); actionSheetOpen = false;">
                    <font-awesome-icon icon="fa-solid fa-image" class="action-sheet__icon" />
                    <span>Schimba fundalul</span>
                </button>

                <button class="action-sheet__item" @click.stop="$refs.sectionBgPicker.click()">
                    <font-awesome-icon icon="fa-solid fa-palette" class="action-sheet__icon" />
                    <span>Culoare fundal pagina</span>
                    <input
                        ref="sectionBgPicker"
                        type="color"
                        :value="sectionBgColor || '#cbd5e1'"
                        class="action-sheet__color-input"
                        @input="sectionBgColor = $event.target.value; saveProjectData();"
                        @click.stop
                    />
                </button>

                <button class="action-sheet__item" @click.stop="openStickerPicker(); actionSheetOpen = false;">
                    <font-awesome-icon icon="fa-solid fa-note-sticky" class="action-sheet__icon" />
                    <span>Adauga sticker</span>
                </button>

                <template v-if="isAdmin">
                    <div class="action-sheet__divider"></div>

                    <!-- Mask mode toggle -->
                    <button class="action-sheet__item" @click.stop="toggleMaskMode(); actionSheetOpen = false;">
                        <font-awesome-icon :icon="maskMode ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off'" class="action-sheet__icon" />
                        <span>{{ maskMode ? 'Dezactiveaza rama foto' : 'Activeaza rama foto' }}</span>
                    </button>

                    <!-- Photo management (only in mask mode) -->
                    <template v-if="maskMode">
                        <button class="action-sheet__item" @click.stop="openPhotoFile(); actionSheetOpen = false;">
                            <font-awesome-icon icon="fa-solid fa-camera" class="action-sheet__icon" />
                            <span>{{ userPhoto.src ? 'Schimba fotografia' : 'Adauga fotografie' }}</span>
                        </button>
                        <button v-if="userPhoto.src" class="action-sheet__item action-sheet__item--danger" @click.stop="removeUserPhoto(); actionSheetOpen = false;">
                            <font-awesome-icon icon="fa-solid fa-trash-can" class="action-sheet__icon" />
                            <span>Sterge fotografia</span>
                        </button>
                    </template>
                </template>

                <div class="action-sheet__divider"></div>

                <button class="action-sheet__item action-sheet__item--danger" @click.stop="resetPositions(); actionSheetOpen = false;">
                    <font-awesome-icon icon="fa-solid fa-rotate-left" class="action-sheet__icon" />
                    <span>Reseteaza pozitiile</span>
                </button>
            </div>
        </transition>

        <!-- Sticker picker overlay -->
        <transition name="action-sheet-fade">
            <div
                v-if="stickerPickerOpen"
                class="action-sheet-overlay"
                @click.stop="stickerPickerOpen = false"
            ></div>
        </transition>

        <!-- Sticker picker panel -->
        <transition name="action-sheet-slide">
            <div
                v-if="stickerPickerOpen"
                class="sticker-picker-panel"
                @click.stop
            >
                <div class="action-sheet__header">
                    <span class="action-sheet__title">Alege sticker</span>
                    <button class="action-sheet__close" @click.stop="stickerPickerOpen = false">
                        <font-awesome-icon icon="fa-solid fa-xmark" />
                    </button>
                </div>

                <div v-if="!stickerCategoriesLoaded" class="sticker-picker-loading">
                    Se incarca...
                </div>

                <div v-else-if="stickerCategories.length === 0" class="sticker-picker-loading">
                    Nu exista stickere disponibile.
                </div>

                <div v-else class="sticker-picker-body">
                    <div
                        v-for="cat in stickerCategories"
                        :key="cat.name"
                        class="sticker-picker-category"
                    >
                        <div class="sticker-picker-category__name">{{ cat.name }}</div>
                        <div class="sticker-picker-grid">
                            <button
                                v-for="sticker in cat.stickers"
                                :key="sticker.path"
                                class="sticker-picker-item"
                                @click.stop="addStickerToCanvas(sticker.path)"
                            >
                                <img :src="sticker.path" :alt="sticker.filename" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Fixed bottom edit bar (visible when a text element is selected) -->
        <transition name="bottom-bar-slide">
            <div
                v-if="isOwner && selectedElement"
                ref="controlBar"
                class="bottom-edit-bar"
                @click.stop
            >
                <div
                    ref="bbarInner"
                    class="bottom-edit-bar__inner"
                    @mousedown="startBarDrag"
                    @touchstart="startBarDrag"
                >
                    <!-- Delete (trash) -->
                    <button class="bbar-item bbar-item--danger" @click.stop="deleteText">
                        <font-awesome-icon icon="fa-solid fa-trash-can" />
                        <span class="bbar-label">Delete</span>
                    </button>

                    <div class="bbar-divider"></div>

                    <!-- Edit text -->
                    <button class="bbar-item" @click.stop="openInlineTextEdit">
                        <font-awesome-icon icon="fa-solid fa-i-cursor" />
                        <span class="bbar-label">Edit text</span>
                    </button>

                    <!-- Font -->
                    <button
                        class="bbar-item"
                        :class="{ 'bbar-item--active': fontPickerOpen }"
                        @click.stop="toggleFontPicker"
                    >
                        <font-awesome-icon icon="fa-solid fa-font" />
                        <span class="bbar-label">Font</span>
                    </button>

                    <!-- Size -->
                    <button class="bbar-item" @click.stop="openSizePopover">
                        <font-awesome-icon icon="fa-solid fa-text-height" />
                        <span class="bbar-label">Size</span>
                    </button>

                    <!-- Color -->
                    <div class="bbar-item bbar-item--color" @click.stop="openColorPicker">
                        <span class="bbar-color-chip" :style="{ backgroundColor: selectedColor }"></span>
                        <input
                            ref="colorInput"
                            type="color"
                            v-model="selectedColor"
                            class="bbar-color-input"
                            @click.stop
                        />
                        <span class="bbar-label">Color</span>
                    </div>

                    <!-- Bold -->
                    <button
                        class="bbar-item"
                        :class="{ 'bbar-item--active': selectedElement.fontWeight === 'font-bold' }"
                        @click.stop="toggleBold"
                    >
                        <font-awesome-icon icon="fa-solid fa-bold" />
                        <span class="bbar-label">Bold</span>
                    </button>

                    <!-- Italic -->
                    <button
                        class="bbar-item"
                        :class="{ 'bbar-item--active': selectedElement.fontStyle === 'italic' }"
                        @click.stop="toggleItalic"
                    >
                        <font-awesome-icon icon="fa-solid fa-italic" />
                        <span class="bbar-label">Italic</span>
                    </button>

                    <!-- Align -->
                    <button class="bbar-item" @click.stop="cycleAlignment">
                        <font-awesome-icon :icon="alignIcon" />
                        <span class="bbar-label">Align</span>
                    </button>

                    <!-- Line height -->
                    <button class="bbar-item" @click.stop="cycleLineHeight">
                        <font-awesome-icon icon="fa-solid fa-arrows-up-down" />
                        <span class="bbar-label">Line height</span>
                    </button>

                    <!-- Spacing -->
                    <button class="bbar-item" @click.stop="cycleLetterSpacing">
                        <font-awesome-icon icon="fa-solid fa-text-width" />
                        <span class="bbar-label">Spacing</span>
                    </button>

                    <!-- Caps -->
                    <button
                        class="bbar-item"
                        :class="{ 'bbar-item--active': selectedElement.textTransform === 'uppercase' }"
                        @click.stop="toggleCaps"
                    >
                        <span class="bbar-icon-text">Aa</span>
                        <span class="bbar-label">Caps</span>
                    </button>

                    <!-- Arc (curved) -->
                    <button
                        class="bbar-item"
                        :class="{ 'bbar-item--active': selectedElement.curved }"
                        @click.stop="toggleCurvedFromBar"
                    >
                        <span class="bbar-icon-text">A͜B</span>
                        <span class="bbar-label">Arc</span>
                    </button>

                    <!-- Duplicate -->
                    <button class="bbar-item" @click.stop="duplicateText">
                        <font-awesome-icon icon="fa-solid fa-copy" />
                        <span class="bbar-label">Duplicate</span>
                    </button>

                    <div class="bbar-divider"></div>

                    <!-- Layer ordering -->
                    <button class="bbar-item" @click.stop="bringForward(selectedElement)">
                        <font-awesome-icon icon="fa-solid fa-arrow-up" />
                        <span class="bbar-label">Forward</span>
                    </button>

                    <button class="bbar-item" @click.stop="sendBackward(selectedElement)">
                        <font-awesome-icon icon="fa-solid fa-arrow-down" />
                        <span class="bbar-label">Back</span>
                    </button>
                </div>
            </div>
        </transition>

        <!-- Photo control bar (visible when user photo is selected in mask mode) -->
        <transition name="bottom-bar-slide">
            <div
                v-if="isOwner && maskMode && photoSelected && userPhoto.src"
                ref="photoControlBar"
                class="bottom-edit-bar"
                @click.stop
            >
                <div class="photo-controls">
                    <!-- Row 1: sliders -->
                    <div class="photo-controls__sliders">
                        <div class="bbar-slider-group" @click.stop>
                            <font-awesome-icon icon="fa-solid fa-magnifying-glass" class="bbar-slider-icon" />
                            <input
                                type="range"
                                class="bbar-range"
                                min="20"
                                max="500"
                                step="1"
                                :value="Math.round(userPhoto.scale * 100)"
                                @input="setPhotoScale($event.target.value / 100)"
                                @change="saveProjectData()"
                            />
                            <span class="bbar-slider-value">{{ Math.round(userPhoto.scale * 100) }}%</span>
                        </div>

                        <div class="bbar-slider-group" @click.stop>
                            <font-awesome-icon icon="fa-solid fa-rotate-right" class="bbar-slider-icon" />
                            <input
                                type="range"
                                class="bbar-range"
                                min="-180"
                                max="180"
                                step="1"
                                :value="userPhoto.rotation || 0"
                                @input="setPhotoRotation(Number($event.target.value))"
                                @change="saveProjectData()"
                            />
                            <span class="bbar-slider-value">{{ userPhoto.rotation || 0 }}°</span>
                        </div>
                    </div>

                    <!-- Row 2: action buttons -->
                    <div class="photo-controls__actions">
                        <button class="bbar-item" @click.stop="resetPhotoTransform">
                            <font-awesome-icon icon="fa-solid fa-arrows-to-dot" />
                            <span class="bbar-label">Reset</span>
                        </button>

                        <button class="bbar-item" @click.stop="openPhotoFile">
                            <font-awesome-icon icon="fa-solid fa-camera" />
                            <span class="bbar-label">Change</span>
                        </button>

                        <button class="bbar-item bbar-item--danger" @click.stop="removeUserPhoto">
                            <font-awesome-icon icon="fa-solid fa-trash-can" />
                            <span class="bbar-label">Delete</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Sticker control bar (visible when a sticker is selected) -->
        <transition name="bottom-bar-slide">
            <div
                v-if="isOwner && selectedStickerId"
                ref="stickerControlBar"
                class="bottom-edit-bar"
                @click.stop
            >
                <div class="photo-controls">
                    <div class="photo-controls__sliders">
                        <div class="bbar-slider-group" @click.stop>
                            <font-awesome-icon icon="fa-solid fa-rotate-right" class="bbar-slider-icon" />
                            <input
                                type="range"
                                class="bbar-range"
                                min="-180"
                                max="180"
                                step="1"
                                :value="selectedStickerRotation"
                                @input="setStickerRotation(Number($event.target.value))"
                                @change="saveProjectData()"
                            />
                            <span class="bbar-slider-value">{{ selectedStickerRotation }}°</span>
                        </div>
                    </div>
                    <div class="photo-controls__actions">
                        <button class="bbar-item" @click.stop="resetStickerRotation">
                            <font-awesome-icon icon="fa-solid fa-arrows-to-dot" />
                            <span class="bbar-label">Reset</span>
                        </button>
                        <button class="bbar-item" @click.stop="toggleStickerFlip">
                            <font-awesome-icon icon="fa-solid fa-left-right" />
                            <span class="bbar-label">Mirror</span>
                        </button>

                        <div class="bbar-divider"></div>

                        <button class="bbar-item" @click.stop="bringForward(selectedStickerObj)">
                            <font-awesome-icon icon="fa-solid fa-arrow-up" />
                            <span class="bbar-label">Forward</span>
                        </button>
                        <button class="bbar-item" @click.stop="sendBackward(selectedStickerObj)">
                            <font-awesome-icon icon="fa-solid fa-arrow-down" />
                            <span class="bbar-label">Back</span>
                        </button>

                        <div class="bbar-divider"></div>

                        <button class="bbar-item bbar-item--danger" @click.stop="deleteSelectedSticker">
                            <font-awesome-icon icon="fa-solid fa-trash-can" />
                            <span class="bbar-label">Delete</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Inline text edit modal -->
        <div v-if="inlineTextEditOpen" class="inline-text-overlay" @click.stop="closeInlineTextEdit">
            <div class="inline-text-modal" @click.stop>
                <textarea
                    ref="inlineTextInput"
                    v-model="selectedText"
                    class="inline-text-modal__input"
                    placeholder="Edit text"
                    rows="3"
                ></textarea>
                <button class="inline-text-modal__ok" @click.stop="closeInlineTextEdit">Actualizeaza</button>
            </div>
        </div>

        <!-- Size popover -->
        <div v-if="sizePopoverOpen" class="inline-text-overlay" @click.stop="closeSizePopover">
            <div class="size-popover" @click.stop>
                <button class="size-popover__btn" @click.stop="changeFontSize(-1)">−</button>
                <span class="size-popover__value">{{ selectedElement.fontSize }}</span>
                <button class="size-popover__btn" @click.stop="changeFontSize(1)">+</button>
            </div>
        </div>

        <!-- Font picker panel -->
        <transition name="font-panel-slide">
            <div
                v-if="fontPickerOpen && selectedElement"
                class="font-picker-panel"
                @click.stop
            >
                <div class="font-picker-panel__header">
                    <span class="font-picker-panel__title">Used in design</span>
                </div>
                <div class="font-picker-panel__list">
                    <button
                        v-for="f in fontMap"
                        :key="f.key"
                        class="font-picker-panel__item"
                        :class="{ 'font-picker-panel__item--active': selectedElement.fontFamily === f.key }"
                        :style="{ fontFamily: f.css }"
                        @click.stop="selectFont(f.key)"
                    >
                        <span class="font-picker-panel__font-name">{{ f.label }}</span>
                        <font-awesome-icon
                            v-if="selectedElement.fontFamily === f.key"
                            icon="fa-solid fa-check"
                            class="font-picker-panel__check"
                        />
                    </button>
                </div>
            </div>
        </transition>

            <!-- Canvas Container -->
            <div class="flex justify-center">
                <div
                    ref="canvasContainer"
                    class="canvas-container relative rounded-xl shadow-xl max-h-screen md:max-h-[90vh] max-w-screen m-2"
                    style="aspect-ratio: 2 / 3; width: auto;"
                >
                    <!-- Attach click handler here to clear selection on background clicks -->
                    <div
                        class="center-frame w-full h-full relative overflow-hidden"
                        :style="{ opacity: canvasReady ? 1 : 0, transition: 'opacity 0.3s ease' }"
                        @click="clearSelection"
                    >
                        <!-- MASK MODE: User photo layer (behind the frame) -->
                        <div
                            v-if="maskMode"
                            :style="photoZoneStyle"
                        >
                            <!-- User photo (draggable + zoomable) -->
                            <img
                                v-if="userPhoto.src"
                                :src="userPhoto.src"
                                class="user-photo-img"
                                :class="{ 'user-photo-img--selected': isOwner && photoSelected }"
                                :style="[userPhotoTransform, { touchAction: isOwner ? 'none' : 'auto', pointerEvents: isOwner ? 'auto' : 'none' }]"
                                @click.stop="selectPhoto"
                                @mousedown.prevent="startPhotoDrag"
                                @touchstart.prevent="startPhotoDrag"
                                @wheel.prevent="onPhotoWheel"
                            />
                            <!-- Placeholder when no photo (owner only) -->
                            <div
                                v-else-if="isOwner"
                                class="photo-zone-placeholder"
                                @click.stop="openPhotoFile"
                            >
                                <font-awesome-icon icon="fa-solid fa-camera" class="photo-zone-placeholder__icon" />
                                <span class="photo-zone-placeholder__label">Adauga foto</span>
                            </div>
                        </div>

                        <!-- Background / Frame Image -->
                        <img
                            :src="backgroundImage"
                            class="w-full h-full border-0 object-cover"
                            :class="{ 'mask-frame-layer': maskMode }"
                            @load="onBgImageLoad"
                        />

                        <!-- Draggable Text Elements -->
                        <vue-draggable-resizable
                            v-for="element in textElements"
                            :key="element.id"
                            :style="{ zIndex: element.zIndex || 2 }"
                            :x="element.x * scaleFactor"
                            :y="element.y * scaleFactor"
                            :w="getScaledWidth(element)"
                            :h="getScaledHeight(element)"
                            :resizable="isOwner ? true : false"
                            :draggable="isOwner ? true : false"
                            @dragging="
                                (left, top) =>
                                    handleDragging(element, left, top)
                            "
                            @resizing="
                                (left, top, width, height) =>
                                    handleResizing(
                                        element,
                                        left,
                                        top,
                                        width,
                                        height
                                    )
                            "
                            class="absolute"
                            :class="{
                                'pointer-events-none disabled': !isOwner,
                            }"
                        >
                    <!-- Conditional Rendering: Curved vs. Normal Text -->
                    <template v-if="element.curved">
                        <svg
                            :width="getScaledWidth(element)"
                            :height="getScaledHeight(element)"
                            :style="{
                                overflow: 'visible',
                                cursor: isOwner ? 'pointer' : 'default',
                                pointerEvents: isOwner ? 'all' : 'none',
                            }"
                            @click.stop="isOwner && selectElement(element.id, $event)"
                            @dblclick.stop="isOwner && selectAndEdit(element.id, $event)"
                            class="text-element"
                        >
                            <defs>
                                <path
                                    :id="'curvePath' + element.id"
                                    :d="getCurvedPath(element)"
                                />
                            </defs>
                            <text
                                :fill="element.color"
                                :font-size="element.fontSize * scaleFactor"
                                :font-family="fontCss(element.fontFamily)"
                                :font-weight="
                                    element.fontWeight === 'font-bold'
                                        ? 'bold'
                                        : 'normal'
                                "
                                :font-style="element.fontStyle"
                                text-anchor="middle"
                                :style="{ cursor: isOwner ? 'pointer' : 'default', pointerEvents: isOwner ? 'all' : 'none' }"
                            >
                                <textPath
                                    :href="'#curvePath' + element.id"
                                    startOffset="50%"
                                >
                                    {{ element.text }}
                                </textPath>
                            </text>
                        </svg>
                    </template>
                    <template v-else>
                        <p
                            @click.stop="isOwner && selectElement(element.id, $event)"
                            @dblclick.stop="isOwner && selectAndEdit(element.id, $event)"
                            class="text-element"
                            :class="[
                                `font-${element.fontFamily}`,
                                element.fontStyle,
                                element.fontWeight,
                                {
                                    'border border-blue-500':
                                        element.id === selectedElementId,
                                },
                            ]"
                            :style="{
                                color: element.color,
                                fontSize: `${element.fontSize * scaleFactor}px`,
                                fontFamily: fontCss(element.fontFamily),
                                textAlign: element.textAlign || 'left',
                                lineHeight: element.lineHeight || 1.4,
                                letterSpacing: (element.letterSpacing || 0) + 'px',
                                textTransform: element.textTransform || 'none',
                                cursor: isOwner ? 'pointer' : 'default',
                            }"
                        >
                            {{ element.text }}
                        </p>
                    </template>
                        </vue-draggable-resizable>

                        <!-- Draggable Stickers -->
                        <vue-draggable-resizable
                            v-for="sticker in stickers"
                            :key="sticker.id"
                            :style="{ zIndex: sticker.zIndex || 3 }"
                            :x="sticker.x * scaleFactor"
                            :y="sticker.y * scaleFactor"
                            :w="sticker.width * scaleFactor"
                            :h="sticker.height * scaleFactor"
                            :lock-aspect-ratio="true"
                            :resizable="isOwner"
                            :draggable="isOwner"
                            @dragging="(left, top) => handleStickerDrag(sticker, left, top)"
                            @resizing="(left, top, width, height) => handleStickerResize(sticker, left, top, width, height)"
                            class="absolute sticker-element"
                            :class="{
                                'pointer-events-none disabled': !isOwner,
                                'sticker-element--selected': sticker.id === selectedStickerId,
                            }"
                        >
                            <img
                                :src="sticker.src"
                                class="sticker-img"
                                :style="{ transform: 'rotate(' + (sticker.rotation || 0) + 'deg)' + (sticker.flipped ? ' scaleX(-1)' : '') }"
                                @click.stop="isOwner && selectSticker(sticker.id)"
                                draggable="false"
                            />
                        </vue-draggable-resizable>
                    </div>
                </div>
            </div>

    <!-- Background-cropper modal (NEW) -->
    <div
        v-if="cropModalOpen && isOwner"
        class="fixed inset-0 flex items-center justify-center z-50"
    >
        <!-- Modal overlay -->
        <div class="absolute inset-0 opacity-50" @click="closeCropModal"></div>
        <!-- Modal content -->
        <div class="relative bg-white p-4 rounded-md shadow-lg z-50 crop-modal-content">
            <button
                class="absolute top-6 right-7 text-white z-50"
                @click="closeCropModal"
            >
                <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>

            <div v-if="!cropImageSrc" class="text-center text-gray-700">
                Selectează o imagine…
            </div>

            <div v-else>
                <cropper
                    ref="bgCropper"
                    :src="cropImageSrc"
                    class="crop-modal-cropper"
                    :stencil-props="{
                        movable: true,
                        resizable: true,
                        aspectRatio: 2 / 3 /* 600×900 px */,
                    }"
                />

                <div class="mt-4 flex justify-end gap-3">
                    <button
                        @click="closeCropModal"
                        class="btn btn-secondary btn-outline"
                    >
                        Anuleaza
                    </button>
                    <button @click="confirmCrop" class="btn btn-primary">
                        Salveaza
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Photo-cropper modal (mask mode) -->
    <div
        v-if="isOwner && photoCropModalOpen"
        class="fixed inset-0 flex items-center justify-center z-50"
    >
        <div class="absolute inset-0 opacity-50" @click="closePhotoCropModal"></div>
        <div class="relative bg-white p-4 rounded-md shadow-lg z-50 crop-modal-content">
            <button
                class="absolute top-6 right-7 text-white z-50"
                @click="closePhotoCropModal"
            >
                <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>

            <div v-if="!photoCropImageSrc" class="text-center text-gray-700">
                Selectează o imagine…
            </div>

            <div v-else>
                <cropper
                    ref="photoCropper"
                    :src="photoCropImageSrc"
                    class="crop-modal-cropper"
                    :stencil-props="{
                        movable: true,
                        resizable: true,
                    }"
                />

                <div class="mt-4 flex justify-end gap-3">
                    <button
                        @click="closePhotoCropModal"
                        class="btn btn-secondary btn-outline"
                    >
                        Anuleaza
                    </button>
                    <button @click="confirmPhotoCrop" class="btn btn-primary">
                        Salveaza
                    </button>
                </div>
            </div>
        </div>
    </div>
    </div>
</template>

<script>
import VueDraggableResizable from 'vue-draggable-resizable';
import { mapActions, mapGetters } from 'vuex';
import { Cropper } from 'vue-advanced-cropper';
import axios from 'axios';
import html2canvas from 'html2canvas';

export default {
    components: {
        VueDraggableResizable,
        Cropper, // NEW component
    },
    // Accept standalone JSON data from parent as a prop
    props: {
        standaloneJson: {
            type: [String, Array, Object],
            default: null,
        },
        templateId: { default: null },
        isOwner: { default: false },
        isAdmin: { default: false },
        // hideFab removed — FAB is now sticky inside its own section
    },
    emits: ['standaloneJsonData', 'save-template'],
    data() {
        // Default background if none supplied
        const defaultBg = '/project-images/tedibear-sleeping-600-900.jpg';

        // Prepare initial data
        let initialTextElements = [];
        let initialBackground = defaultBg;
        let initialMaskMode = false;
        let initialPhotoZone = { x: 15, y: 20, width: 70, height: 55 };
        let initialUserPhoto = { src: '', offsetX: 0, offsetY: 0, scale: 1, rotation: 0 };
        let initialStickers = [];
        let initialSectionBgColor = '';

        if (this.standaloneJson) {
            try {
                const parsed =
                    typeof this.standaloneJson === 'string'
                        ? JSON.parse(this.standaloneJson)
                        : this.standaloneJson;

                if (Array.isArray(parsed)) {
                    // Old format (just text array)
                    initialTextElements = parsed;
                } else if (
                    parsed &&
                    typeof parsed === 'object' &&
                    parsed.textElements
                ) {
                    // New format with background + text
                    initialTextElements = parsed.textElements;
                    initialBackground =
                        parsed.backgroundImage || initialBackground;
                    // Mask mode fields
                    if (parsed.maskMode) initialMaskMode = true;
                    if (parsed.photoZone) initialPhotoZone = { ...initialPhotoZone, ...parsed.photoZone };
                    if (parsed.userPhoto) initialUserPhoto = { ...initialUserPhoto, ...parsed.userPhoto };
                    if (Array.isArray(parsed.stickers)) initialStickers = parsed.stickers;
                    if (parsed.sectionBgColor) initialSectionBgColor = parsed.sectionBgColor;
                }
            } catch (e) {
                console.error('Error parsing standaloneJson', e);
                initialTextElements = [];
            }
        }

        return {
            textElements: initialTextElements,
            backgroundImage: initialBackground,
            sectionBgColor: initialSectionBgColor,
            // Mask mode: PNG frame with user photo behind it
            maskMode: initialMaskMode,
            photoZone: initialPhotoZone,
            userPhoto: initialUserPhoto,
            // Photo cropper state
            photoCropModalOpen: false,
            photoCropImageSrc: null,
            photoCropImageType: null,
            // Photo selection state
            photoSelected: false,
            // Photo drag state
            photoDragging: false,
            photoDragStartX: 0,
            photoDragStartY: 0,
            photoStartOffsetX: 0,
            photoStartOffsetY: 0,
            // NEW cropper modal state
            cropModalOpen: false,
            cropImageSrc: null,
            cropImageType: null,
            // END new state
            // Stickers
            stickers: initialStickers,
            stickerPickerOpen: false,
            stickerCategories: [],
            stickerCategoriesLoaded: false,
            selectedStickerId: null,
            //
            canvasReady: false,
            scaleFactor: 1,
            baseWidth: 600,
            selectedElementId: null,
            inlineTextEditOpen: false,
            sizePopoverOpen: false,
            fontPickerOpen: false,
            actionSheetOpen: false,
            lastSavedSnapshot: null,
            fontDropdownPosition: { x: 0, y: 0 },
            availableFonts: [
                'sans-serif',
                'serif',
                'monospace',
                'cursive',
            ],
        };
    },
    computed: {
        ...mapGetters(['isEditMode']),
        editMode() {
            return this.isEditMode;
        },
        photoZoneStyle() {
            const z = this.photoZone;
            return {
                position: 'absolute',
                left: z.x + '%',
                top: z.y + '%',
                width: z.width + '%',
                height: z.height + '%',
                overflow: 'hidden',
                zIndex: 0,
            };
        },
        userPhotoTransform() {
            const p = this.userPhoto;
            const rot = p.rotation || 0;
            return {
                transform: `translate(${p.offsetX}px, ${p.offsetY}px) scale(${p.scale}) rotate(${rot}deg)`,
                transformOrigin: 'center center',
            };
        },
        selectedStickerRotation() {
            const s = this.stickers.find(s => s.id === this.selectedStickerId);
            return s ? (s.rotation || 0) : 0;
        },
        selectedStickerObj() {
            return this.stickers.find(s => s.id === this.selectedStickerId) || null;
        },
        nextZIndex() {
            let max = 1;
            for (const el of this.textElements) {
                if ((el.zIndex || 0) > max) max = el.zIndex;
            }
            for (const s of this.stickers) {
                if ((s.zIndex || 0) > max) max = s.zIndex;
            }
            return max + 1;
        },
        // Returns the currently selected text element object
        selectedElement() {
            return this.textElements.find(
                (el) => el.id === this.selectedElementId
            );
        },
        // Computed property for the text value with getter and setter
        selectedText: {
            get() {
                return this.selectedElement ? this.selectedElement.text : '';
            },
            set(value) {
                if (this.isOwner) {
                    if (this.selectedElement) {
                        this.selectedElement.text = value;
                        this.saveProjectData();
                    }
                } else {
                    return false;
                }
            },
        },
        fontMap() {
            const genericFonts = [
                { key: 'sans-serif', label: 'Sans Serif', css: 'sans-serif' },
                { key: 'serif', label: 'Serif', css: 'serif' },
                { key: 'monospace', label: 'Monospace', css: 'monospace' },
                { key: 'cursive', label: 'Cursive', css: 'cursive' },
            ];
            // Build entries from availableFonts (API-driven), skipping the 4 generics already added
            const generics = new Set(['sans-serif', 'serif', 'monospace', 'cursive']);
            const apiEntries = this.availableFonts
                .filter(f => !generics.has(f))
                .map(cssVal => {
                    // cssVal is like "'Alex Brush', cursive"
                    const name = cssVal.split(',')[0].replace(/'/g, '').trim();
                    const key = name.toLowerCase().replace(/\s+/g, '-');
                    return { key, label: name, css: cssVal };
                });
            return [...genericFonts, ...apiEntries];
        },
        hasUnsavedChanges() {
            if (!this.lastSavedSnapshot) return false;
            return this.getLatestJsonData() !== this.lastSavedSnapshot;
        },
        alignIcon() {
            if (!this.selectedElement) return 'fa-solid fa-align-left';
            const a = this.selectedElement.textAlign || 'left';
            if (a === 'center') return 'fa-solid fa-align-center';
            if (a === 'right') return 'fa-solid fa-align-right';
            return 'fa-solid fa-align-left';
        },
        // Computed property for the color value with getter and setter
        selectedColor: {
            get() {
                return this.selectedElement
                    ? this.selectedElement.color
                    : '#000000';
            },
            set(value) {
                if (this.selectedElement) {
                    this.selectedElement.color = value;
                    this.saveProjectData();
                }
            },
        },
    },
    methods: {
        ...mapActions(['toggleEditMode']),
        async onBgImageLoad() {
            this.updateScaleFactor();
            // Wait for font API data (populates fontMap so fontCss() can resolve keys)
            try {
                await this._fontsReady;
            } catch (_) { /* ignore */ }
            // Wait for all @font-face files to finish downloading
            try {
                await document.fonts.ready;
            } catch (_) { /* ignore */ }
            this.updateScaleFactor();
            this.canvasReady = true;
        },
        fontCss(key) {
            // Strip any stray quotes from key (stored data may have trailing ')
            const cleanKey = key.replace(/'/g, '').trim();
            const entry = this.fontMap.find(f => f.key === cleanKey);
            return entry ? entry.css : cleanKey;
        },
        async loadFontsFromAPI() {
            try {
                const res = await axios.get('/api/index.php?endpoint=fonts');
                if (res.data && res.data.success && Array.isArray(res.data.data)) {
                    const apiFonts = res.data.data.map(f => `'${f.css_value}', ${f.category}`);
                    // Keep the 4 generic fallbacks, append API fonts
                    this.availableFonts = [
                        'sans-serif',
                        'serif',
                        'monospace',
                        'cursive',
                        ...apiFonts,
                    ];
                }
            } catch (err) {
                console.error('Failed to fetch fonts from API', err);
            }
            // Load self-hosted @font-face CSS
            const existingLink = document.getElementById('app-fonts-css');
            if (!existingLink) {
                const link = document.createElement('link');
                link.id = 'app-fonts-css';
                link.rel = 'stylesheet';
                link.href = '/api/index.php?endpoint=fontsCSS';
                document.head.appendChild(link);
            }
        },
        emitSave() {
            this.$emit('save-template');
            this.lastSavedSnapshot = this.getLatestJsonData();
        },
        async getInitialTextElements() {
            const jwtToken = localStorage.getItem('userToken');
            try {
                const response = await fetch(
                    `/api/index.php?endpoint=getDefaultTemplateContent&templateId=${this.templateId}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${jwtToken}`,
                        },
                    }
                );
                const data = await response.json();
                if (data.success && data.data && data.data.standalone_json) {
                    const parsed =
                        typeof data.data.standalone_json === 'string'
                            ? JSON.parse(data.data.standalone_json)
                            : data.data.standalone_json;

                    if (Array.isArray(parsed)) {
                        return { backgroundImage: null, textElements: parsed };
                    }
                    return parsed;
                } else {
                    console.error(
                        'Error fetching default template data:',
                        data.message
                    );
                    return { backgroundImage: null, textElements: [] };
                }
            } catch (error) {
                console.error('Fetch error:', error);
                return { backgroundImage: null, textElements: [] };
            }
        },
        // Updates the position, width, and height of a text element
        updatePosition(id, newX, newY, newW, newH) {
            if (this.isOwner) {
                const element = this.textElements.find((el) => el.id === id);
                if (element) {
                    element.x = newX;
                    element.y = newY;
                    element.width = newW;
                    element.height = newH;
                    this.saveProjectData();
                }
            }
        },
        // Resets text elements to their initial state
        async resetPositions() {
            try {
                const { backgroundImage, textElements } =
                    await this.getInitialTextElements();
                if (backgroundImage) this.backgroundImage = backgroundImage;
                this.textElements = textElements;
                this.selectedElementId = null;
                this.saveProjectData();
            } catch (error) {
                console.error('Error resetting positions:', error);
                this.textElements = [];
            }
        },
        // Updates the scale factor based on the canvas container's width
        updateScaleFactor() {
            const container = this.$refs.canvasContainer;
            const containerWidth = container && container.clientWidth;
            // Ensure a valid container width so scaleFactor is never 0
            this.scaleFactor =
                containerWidth && containerWidth > 0
                    ? containerWidth / this.baseWidth
                    : 1;
        },
        // Selects an element when clicked and positions the font dropdown
        selectElement(id, event) {
            if (this.isOwner) {
                this.selectedElementId = id;
                this.fontDropdownPosition = {
                    x: event.clientX,
                    y: event.clientY,
                };
            }
        },
        // Selects an element and immediately opens the text edit modal (double-click)
        selectAndEdit(id, event) {
            if (this.isOwner) {
                this.selectedElementId = id;
                this.fontDropdownPosition = {
                    x: event.clientX,
                    y: event.clientY,
                };
                this.openInlineTextEdit();
            }
        },
        // Changes the font of the selected element
        changeFont(event) {
            // Instead of replacing the object, update the property directly
            if (this.selectedElement) {
                this.selectedElement.fontFamily = event.target.value;
                this.saveProjectData();
            }
        },
        toggleFontPicker() {
            this.fontPickerOpen = !this.fontPickerOpen;
        },
        selectFont(fontKey) {
            if (this.selectedElement) {
                this.selectedElement.fontFamily = fontKey;
                this.saveProjectData();
            }
            this.fontPickerOpen = false;
        },
        // Changes the font size of the selected element
        changeFontSize(amount) {
            if (this.selectedElement) {
                this.selectedElement.fontSize = Math.max(
                    10,
                    this.selectedElement.fontSize + amount
                );
                this.saveProjectData();
            }
        },
        // Toggles bold styling for the selected element
        toggleBold() {
            if (this.selectedElement) {
                this.selectedElement.fontWeight =
                    this.selectedElement.fontWeight === 'font-bold'
                        ? 'font-normal'
                        : 'font-bold';
                this.saveProjectData();
            }
        },
        // Toggles italic styling for the selected element
        toggleItalic() {
            if (this.selectedElement) {
                this.selectedElement.fontStyle =
                    this.selectedElement.fontStyle === 'italic'
                        ? 'normal'
                        : 'italic';
                this.saveProjectData();
            }
        },
        // Changes the text alignment for the selected element
        changeAlignment(alignment) {
            if (this.selectedElement) {
                this.selectedElement.textAlign = alignment;
                this.saveProjectData();
            }
        },
        openBackgroundFile() {
            const input = this.$refs.bgInput;
            if (input) input.click();
        },
        openColorPicker() {
            const input = this.$refs.colorInput;
            if (input) {
                input.click();
            }
        },
        // Duplicates the selected text element with a new unique id
        duplicateText() {
            if (this.selectedElement) {
                const newElement = {
                    ...this.selectedElement,
                    id: Date.now(),
                    x: this.selectedElement.x + 10,
                    y: this.selectedElement.y + 10,
                    zIndex: this.nextZIndex,
                };
                this.textElements.push(newElement);
                this.saveProjectData();
            }
        },
        // Deletes the selected text element
        deleteText() {
            if (this.selectedElement) {
                this.textElements = this.textElements.filter(
                    (el) => el.id !== this.selectedElement.id
                );
                this.selectedElementId = null;
                this.saveProjectData();
            }
        },
        // Adds a new text element with default values
        addText() {
            // The coordinate system uses negative Y values (origin is offset).
            // Compute center of visible canvas in base coordinates.
            const container = this.$refs.canvasContainer;
            let baseX = 100;
            let baseY = -450; // sensible default near vertical center
            if (container && this.scaleFactor > 0) {
                const baseH = container.clientHeight / this.scaleFactor;
                baseX = (container.clientWidth / 2 / this.scaleFactor) - 200;
                baseY = -(baseH / 2);
            }
            const newElement = {
                id: Date.now(),
                text: 'Text nou',
                x: Math.max(0, baseX),
                y: baseY,
                fontSize: 24,
                fontStyle: 'normal',
                fontFamily: 'sans-serif',
                color: '#6D4B3D',
                width: 400,
                height: 50,
                curved: false,
                zIndex: this.nextZIndex,
            };
            this.textElements.push(newElement);
            this.saveProjectData();
            this.selectedElementId = newElement.id;
        },
        // Centralised save for both text & background
        saveProjectData() {
            const payload = {
                backgroundImage: this.backgroundImage,
                textElements: this.textElements,
            };
            if (this.maskMode) {
                payload.maskMode = true;
                payload.photoZone = this.photoZone;
                payload.userPhoto = this.userPhoto;
            }
            if (this.stickers.length > 0) {
                payload.stickers = this.stickers;
            }
            if (this.sectionBgColor) {
                payload.sectionBgColor = this.sectionBgColor;
            }
            this.$emit('standaloneJsonData', JSON.stringify(payload));
        },
        getLatestJsonData() {
            const payload = {
                backgroundImage: this.backgroundImage,
                textElements: this.textElements,
            };
            if (this.maskMode) {
                payload.maskMode = true;
                payload.photoZone = this.photoZone;
                payload.userPhoto = this.userPhoto;
            }
            if (this.stickers.length > 0) {
                payload.stickers = this.stickers;
            }
            if (this.sectionBgColor) {
                payload.sectionBgColor = this.sectionBgColor;
            }
            return JSON.stringify(payload);
        },
        clearSelection() {
            this.selectedElementId = null;
            this.fontPickerOpen = false;
            this.photoSelected = false;
            this.selectedStickerId = null;
        },
        handleClickOutside(event) {
            if (!this.selectedElementId && !this.photoSelected && !this.selectedStickerId) return;

            const controlBar = this.$refs.controlBar;
            if (controlBar && controlBar.contains(event.target)) {
                return;
            }

            const photoControlBar = this.$refs.photoControlBar;
            if (photoControlBar && photoControlBar.contains(event.target)) {
                return;
            }

            const stickerControlBar = this.$refs.stickerControlBar;
            if (stickerControlBar && stickerControlBar.contains(event.target)) {
                return;
            }

            const isFontPanel = event.target.closest('.font-picker-panel');
            if (isFontPanel) return;

            const isActionSheet = event.target.closest('.action-sheet') || event.target.closest('.action-sheet-overlay');
            if (isActionSheet) return;

            const isTextElement = event.target.closest('.text-element');
            const isDraggable = event.target.closest('.vdr');
            if (isTextElement || isDraggable) {
                return;
            }

            const isUserPhoto = event.target.closest('.user-photo-img');
            if (isUserPhoto) return;

            const isStickerElement = event.target.closest('.sticker-element');
            if (isStickerElement) return;

            const isStickerPicker = event.target.closest('.sticker-picker-panel');
            if (isStickerPicker) return;

            this.clearSelection();
        },
        // Handles dragging events (parameters passed by vue-draggable-resizable)
        handleDragging(element, left, top) {
            if (this.isOwner) {
                this.updatePosition(
                    element.id,
                    left / this.scaleFactor,
                    top / this.scaleFactor,
                    element.width,
                    element.height
                );
            }
        },
        // Handles resizing events (parameters passed by vue-draggable-resizable)
        handleResizing(element, left, top, width, height) {
            if (this.isOwner) {
                this.updatePosition(
                    element.id,
                    left / this.scaleFactor,
                    top / this.scaleFactor,
                    width / this.scaleFactor,
                    height / this.scaleFactor
                );
            }
        },
        // Calculate scaled width ensuring it's at least 1 for validation
        getScaledWidth(element) {
            const scaled = element.width * this.scaleFactor;
            return scaled > 0 ? scaled : 1;
        },
        // Calculate scaled height ensuring it's at least 1 for validation
        getScaledHeight(element) {
            const scaled = element.height * this.scaleFactor;
            return scaled > 0 ? scaled : 1;
        },
        toggleCurvedText() {
            this.saveProjectData();
        },
        openInlineTextEdit() {
            this.inlineTextEditOpen = true;
            this.$nextTick(() => {
                const input = this.$refs.inlineTextInput;
                if (input) input.focus();
            });
        },
        closeInlineTextEdit() {
            this.inlineTextEditOpen = false;
        },
        openSizePopover() {
            this.sizePopoverOpen = true;
        },
        closeSizePopover() {
            this.sizePopoverOpen = false;
        },
        cycleAlignment() {
            if (!this.selectedElement) return;
            const order = ['left', 'center', 'right'];
            const cur = this.selectedElement.textAlign || 'left';
            const idx = order.indexOf(cur);
            this.selectedElement.textAlign = order[(idx + 1) % order.length];
            this.saveProjectData();
        },
        cycleLineHeight() {
            if (!this.selectedElement) return;
            const steps = [1, 1.2, 1.4, 1.6, 1.8, 2];
            const cur = this.selectedElement.lineHeight || 1.4;
            const idx = steps.indexOf(cur);
            this.selectedElement.lineHeight = steps[(idx + 1) % steps.length];
            this.saveProjectData();
        },
        cycleLetterSpacing() {
            if (!this.selectedElement) return;
            const steps = [0, 1, 2, 3, 5, 8];
            const cur = this.selectedElement.letterSpacing || 0;
            const idx = steps.indexOf(cur);
            this.selectedElement.letterSpacing = steps[(idx + 1) % steps.length];
            this.saveProjectData();
        },
        toggleCaps() {
            if (!this.selectedElement) return;
            this.selectedElement.textTransform =
                this.selectedElement.textTransform === 'uppercase' ? 'none' : 'uppercase';
            this.saveProjectData();
        },
        toggleCurvedFromBar() {
            if (!this.selectedElement) return;
            this.selectedElement.curved = !this.selectedElement.curved;
            this.saveProjectData();
        },
        startBarDrag(e) {
            const container = this.$refs.bbarInner;
            if (!container) return;
            const isTouch = e.type === 'touchstart';
            const startX = isTouch ? e.touches[0].clientX : e.clientX;
            const scrollLeft = container.scrollLeft;
            let moved = false;

            const onMove = (ev) => {
                const x = isTouch ? ev.touches[0].clientX : ev.clientX;
                const dx = startX - x;
                if (Math.abs(dx) > 3) moved = true;
                container.scrollLeft = scrollLeft + dx;
            };

            const onEnd = () => {
                document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
                document.removeEventListener(isTouch ? 'touchend' : 'mouseup', onEnd);
                if (moved) {
                    container.addEventListener('click', (ev) => { ev.stopPropagation(); }, { once: true, capture: true });
                }
            };

            document.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove, { passive: true });
            document.addEventListener(isTouch ? 'touchend' : 'mouseup', onEnd);
        },
        getCurvedPath(element) {
            const scaledWidth = this.getScaledWidth(element);
            const scaledHeight = this.getScaledHeight(element);
            return `M0,${scaledHeight / 2} A${scaledWidth / 2},${
                scaledHeight / 2
            } 0 0,1 ${scaledWidth},${scaledHeight / 2}`;
        },
        // Upload a blob/file to the server, returns the public image path
        async uploadCanvasImage(blob, filename) {
            const jwtToken = localStorage.getItem('userToken');
            const formData = new FormData();
            formData.append('file', blob, filename);
            formData.append('projectId', this.templateId);
            const response = await axios.post(
                '/api/index.php?endpoint=uploadCanvasImage',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${jwtToken}` } }
            );
            if (response.data.success) {
                return response.data.imagePath;
            }
            throw new Error(response.data.message || 'Upload failed');
        },
        // Handle file input → open cropper modal
        onBackgroundChange(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;

            // In mask mode, bypass cropper to preserve PNG alpha channel
            if (this.maskMode && file.type === 'image/png') {
                const img = new Image();
                const blobUrl = URL.createObjectURL(file);
                img.onload = async () => {
                    const tmp = document.createElement('canvas');
                    tmp.width = 600;
                    tmp.height = 900;
                    const ctx = tmp.getContext('2d');
                    ctx.drawImage(img, 0, 0, 600, 900);
                    URL.revokeObjectURL(blobUrl);
                    try {
                        const blob = await new Promise(r => tmp.toBlob(r, 'image/png'));
                        const path = await this.uploadCanvasImage(blob, 'frame.png');
                        this.backgroundImage = path;
                        this.saveProjectData();
                    } catch (err) {
                        console.error('Frame upload failed', err);
                    }
                };
                img.src = blobUrl;
                event.target.value = '';
                return;
            }

            const blobUrl = URL.createObjectURL(file);
            this.cropImageSrc = blobUrl;
            this.cropImageType = file.type;
            this.cropModalOpen = true;
            // Reset native input so selecting same file again triggers change
            event.target.value = '';
        },
        // User confirms crop
        async confirmCrop() {
            const cropper = this.$refs.bgCropper;
            if (!cropper) return;
            const { canvas } = cropper.getResult();
            if (!canvas) return;

            // Guarantee a 600×900 output
            const targetW = 600;
            const targetH = 900;
            const tmp = document.createElement('canvas');
            tmp.width = targetW;
            tmp.height = targetH;
            tmp.getContext('2d').drawImage(canvas, 0, 0, targetW, targetH);

            const mimeType = this.cropImageType || 'image/png';
            const ext = mimeType === 'image/png' ? 'png' : 'jpg';
            try {
                const blob = await new Promise(r => tmp.toBlob(r, mimeType, 0.9));
                const path = await this.uploadCanvasImage(blob, `bg.${ext}`);
                this.backgroundImage = path;
                this.saveProjectData();
            } catch (err) {
                console.error('Background upload failed', err);
            }
            this.closeCropModal();
        },
        // Close cropper modal
        closeCropModal() {
            if (this.cropImageSrc) URL.revokeObjectURL(this.cropImageSrc);
            this.cropModalOpen = false;
            this.cropImageSrc = null;
            this.cropImageType = null;
        },
        // ── Mask-mode photo methods ──
        selectPhoto() {
            this.photoSelected = true;
            this.selectedElementId = null;
            this.fontPickerOpen = false;
        },
        setPhotoScale(val) {
            this.userPhoto = { ...this.userPhoto, scale: parseFloat(val) };
        },
        setPhotoRotation(val) {
            this.userPhoto = { ...this.userPhoto, rotation: val };
        },
        photoZoomStep(delta) {
            const newScale = Math.max(0.2, Math.min(5, this.userPhoto.scale + delta));
            this.userPhoto = { ...this.userPhoto, scale: newScale };
            this.saveProjectData();
        },
        photoRotateStep(deg) {
            const newRot = (this.userPhoto.rotation || 0) + deg;
            this.userPhoto = { ...this.userPhoto, rotation: newRot };
            this.saveProjectData();
        },
        resetPhotoTransform() {
            this.userPhoto = { ...this.userPhoto, offsetX: 0, offsetY: 0, scale: 1, rotation: 0 };
            this.saveProjectData();
        },
        openPhotoFile() {
            const input = this.$refs.photoInput;
            if (input) input.click();
        },
        onUserPhotoChange(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;
            const blobUrl = URL.createObjectURL(file);
            this.photoCropImageSrc = blobUrl;
            this.photoCropImageType = file.type;
            this.photoCropModalOpen = true;
            event.target.value = '';
        },
        async confirmPhotoCrop() {
            const cropper = this.$refs.photoCropper;
            if (!cropper) return;
            const { canvas } = cropper.getResult();
            if (!canvas) return;
            const mimeType = this.photoCropImageType || 'image/jpeg';
            const ext = mimeType === 'image/png' ? 'png' : 'jpg';
            try {
                const blob = await new Promise(r => canvas.toBlob(r, mimeType, 0.9));
                const path = await this.uploadCanvasImage(blob, `photo.${ext}`);
                this.userPhoto = { ...this.userPhoto, src: path, offsetX: 0, offsetY: 0, scale: 1, rotation: 0 };
                this.saveProjectData();
            } catch (err) {
                console.error('Photo upload failed', err);
            }
            this.closePhotoCropModal();
        },
        closePhotoCropModal() {
            if (this.photoCropImageSrc) URL.revokeObjectURL(this.photoCropImageSrc);
            this.photoCropModalOpen = false;
            this.photoCropImageSrc = null;
            this.photoCropImageType = null;
        },
        startPhotoDrag(e) {
            this.selectPhoto();
            this.photoDragging = true;
            const isTouch = e.type === 'touchstart';
            const clientX = isTouch ? e.touches[0].clientX : e.clientX;
            const clientY = isTouch ? e.touches[0].clientY : e.clientY;
            this.photoDragStartX = clientX;
            this.photoDragStartY = clientY;
            this.photoStartOffsetX = this.userPhoto.offsetX;
            this.photoStartOffsetY = this.userPhoto.offsetY;

            const onMove = (ev) => {
                const cx = isTouch ? ev.touches[0].clientX : ev.clientX;
                const cy = isTouch ? ev.touches[0].clientY : ev.clientY;
                this.userPhoto = {
                    ...this.userPhoto,
                    offsetX: this.photoStartOffsetX + (cx - this.photoDragStartX),
                    offsetY: this.photoStartOffsetY + (cy - this.photoDragStartY),
                };
            };

            const onEnd = () => {
                this.photoDragging = false;
                document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
                document.removeEventListener(isTouch ? 'touchend' : 'mouseup', onEnd);
                this.saveProjectData();
            };

            document.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove, { passive: false });
            document.addEventListener(isTouch ? 'touchend' : 'mouseup', onEnd);
        },
        onPhotoWheel(e) {
            const delta = e.deltaY > 0 ? -0.05 : 0.05;
            const newScale = Math.max(0.2, Math.min(5, this.userPhoto.scale + delta));
            this.userPhoto = { ...this.userPhoto, scale: newScale };
            this.saveProjectData();
        },
        toggleMaskMode() {
            this.maskMode = !this.maskMode;
            if (!this.maskMode) {
                this.userPhoto = { src: '', offsetX: 0, offsetY: 0, scale: 1, rotation: 0 };
                this.photoSelected = false;
            }
            this.saveProjectData();
        },
        removeUserPhoto() {
            this.userPhoto = { src: '', offsetX: 0, offsetY: 0, scale: 1, rotation: 0 };
            this.photoSelected = false;
            this.saveProjectData();
        },
        // ── Sticker methods ──
        async openStickerPicker() {
            this.stickerPickerOpen = true;
            if (!this.stickerCategoriesLoaded) {
                await this.fetchStickerCategories();
            }
        },
        async fetchStickerCategories() {
            try {
                const res = await axios.get('/api/index.php?endpoint=getStickers');
                if (res.data && res.data.success) {
                    this.stickerCategories = res.data.categories;
                }
            } catch (err) {
                console.error('Failed to fetch stickers', err);
            }
            this.stickerCategoriesLoaded = true;
        },
        addStickerToCanvas(stickerPath) {
            const container = this.$refs.canvasContainer;
            let baseX = 100;
            let baseY = -450;
            if (container && this.scaleFactor > 0) {
                const baseH = container.clientHeight / this.scaleFactor;
                baseX = (container.clientWidth / 2 / this.scaleFactor) - 50;
                baseY = -(baseH / 2);
            }
            const newSticker = {
                id: 'stk_' + Date.now(),
                src: stickerPath,
                x: baseX,
                y: baseY,
                width: 100,
                height: 100,
                rotation: 0,
                zIndex: this.nextZIndex,
            };
            this.stickers.push(newSticker);
            this.stickerPickerOpen = false;
            this.selectedStickerId = newSticker.id;
            this.selectedElementId = null;
            this.photoSelected = false;
            this.saveProjectData();
        },
        selectSticker(id) {
            this.selectedStickerId = id;
            this.selectedElementId = null;
            this.photoSelected = false;
            this.fontPickerOpen = false;
        },
        handleStickerDrag(sticker, left, top) {
            if (!this.isOwner) return;
            sticker.x = left / this.scaleFactor;
            sticker.y = top / this.scaleFactor;
            this.saveProjectData();
        },
        handleStickerResize(sticker, left, top, width, height) {
            if (!this.isOwner) return;
            sticker.x = left / this.scaleFactor;
            sticker.y = top / this.scaleFactor;
            sticker.width = width / this.scaleFactor;
            sticker.height = height / this.scaleFactor;
            this.saveProjectData();
        },
        setStickerRotation(val) {
            const s = this.stickers.find(s => s.id === this.selectedStickerId);
            if (s) s.rotation = val;
        },
        resetStickerRotation() {
            const s = this.stickers.find(s => s.id === this.selectedStickerId);
            if (s) {
                s.rotation = 0;
                this.saveProjectData();
            }
        },
        toggleStickerFlip() {
            const s = this.stickers.find(s => s.id === this.selectedStickerId);
            if (s) {
                s.flipped = !s.flipped;
                this.saveProjectData();
            }
        },
        deleteSelectedSticker() {
            this.stickers = this.stickers.filter(s => s.id !== this.selectedStickerId);
            this.selectedStickerId = null;
            this.saveProjectData();
        },
        ensureZIndexes() {
            const all = [...this.textElements, ...this.stickers];
            const needsAssign = all.some(el => !el.zIndex);
            if (needsAssign) {
                let z = 10;
                for (const el of this.textElements) {
                    if (!el.zIndex) el.zIndex = z++;
                }
                for (const s of this.stickers) {
                    if (!s.zIndex) s.zIndex = z++;
                }
            }
        },
        bringForward(item) {
            if (!item) return;
            this.ensureZIndexes();
            const all = [...this.textElements, ...this.stickers].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
            const idx = all.findIndex(el => el.id === item.id);
            if (idx < 0 || idx >= all.length - 1) return;
            const above = all[idx + 1];
            const tmp = item.zIndex;
            item.zIndex = above.zIndex;
            above.zIndex = tmp;
            if (item.zIndex === above.zIndex) item.zIndex++;
            this.saveProjectData();
        },
        sendBackward(item) {
            if (!item) return;
            this.ensureZIndexes();
            const all = [...this.textElements, ...this.stickers].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
            const idx = all.findIndex(el => el.id === item.id);
            if (idx <= 0) return;
            const below = all[idx - 1];
            const tmp = item.zIndex;
            item.zIndex = below.zIndex;
            below.zIndex = tmp;
            if (item.zIndex === below.zIndex) below.zIndex++;
            this.saveProjectData();
        },
        async capturePreview() {
            const el = this.$refs.canvasContainer;
            if (!el) {
                console.error('capturePreview: canvasContainer ref not found');
                return null;
            }
            this.clearSelection();
            await this.$nextTick();
            await document.fonts.ready;
            try {
                const canvas = await html2canvas(el, {
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    scale: 1,
                    logging: false,
                    imageTimeout: 5000,
                    onclone: (clonedDoc) => {
                        const all = clonedDoc.querySelectorAll('*');
                        all.forEach(node => {
                            const cs = node.style;
                            if (!cs) return;
                            for (let i = cs.length - 1; i >= 0; i--) {
                                const prop = cs[i];
                                const val = cs.getPropertyValue(prop);
                                if (val && val.includes('oklch')) {
                                    cs.setProperty(prop, 'transparent');
                                }
                            }
                            const computed = clonedDoc.defaultView.getComputedStyle(node);
                            ['background-color', 'color', 'border-color', 'outline-color', 'box-shadow'].forEach(p => {
                                const v = computed.getPropertyValue(p);
                                if (v && v.includes('oklch')) {
                                    cs.setProperty(p, p === 'color' ? '#000000' : 'transparent');
                                }
                            });
                        });
                    },
                });
                return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            } catch (err) {
                console.error('html2canvas error:', err);
                throw err;
            }
        },
    },
    mounted() {
        window.addEventListener('resize', this.updateScaleFactor);
        // Initial scale factor (may be 0 if image not yet loaded; onBgImageLoad will fix it)
        this.$nextTick(() => this.updateScaleFactor());
        // Snapshot initial state for unsaved-changes detection
        this.lastSavedSnapshot = this.getLatestJsonData();
        // Global click handler
        document.addEventListener('click', this.handleClickOutside);
        // Fetch available fonts from API and load @font-face CSS
        // Store promise so onBgImageLoad can await it before revealing canvas
        this._fontsReady = this.loadFontsFromAPI();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateScaleFactor);
        document.removeEventListener('click', this.handleClickOutside);
        if (this.cropImageSrc) URL.revokeObjectURL(this.cropImageSrc);
        if (this.photoCropImageSrc) URL.revokeObjectURL(this.photoCropImageSrc);
    },
};
</script>

<style>
.draggable.vdr:not(.active) {
    border: 0;
}

.vdr.disabled {
    border: 0;
}

/* ── Floating save button (top-right) ── */
.fab-save-btn {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 42;
    height: 36px;
    padding: 0 14px;
    border: none;
    border-radius: 9999px;
    background: #ffffff;
    color: #334155;
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.12);
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease;
}

.fab-save-btn:hover {
    background: #f0fdf4;
    color: #16a34a;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.16);
}

.fab-save-btn--dirty {
    background: #16a34a;
    color: #ffffff;
    box-shadow: 0 4px 16px rgba(22, 163, 74, 0.35);
    animation: fab-pulse 2s infinite;
}

.fab-save-btn--dirty:hover {
    background: #15803d;
    color: #ffffff;
    box-shadow: 0 6px 20px rgba(22, 163, 74, 0.45);
}

@keyframes fab-pulse {
    0%, 100% { box-shadow: 0 4px 16px rgba(22, 163, 74, 0.35); }
    50% { box-shadow: 0 4px 24px rgba(22, 163, 74, 0.55); }
}

/* ── Floating Action Button (+) — absolute top-left of canvas section ── */
.fab-add {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 42;
    width: 48px;
    height: 48px;
    border: none;
    border-radius: 50%;
    background: rgba(22, 163, 74, 0.9);
    color: #ffffff;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 12px rgba(22, 163, 74, 0.3);
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
}

.fab-add:hover {
    background: rgba(21, 128, 61, 0.95);
    box-shadow: 0 4px 16px rgba(22, 163, 74, 0.4);
    transform: scale(1.05);
}

/* ── Section edit button (absolute top-left of each section) ── */
.section-edit-btn {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 30;
    height: 40px;
    padding: 0 16px;
    border: none;
    border-radius: 20px;
    background: rgba(22, 163, 74, 0.9);
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    box-shadow: 0 2px 12px rgba(22, 163, 74, 0.3);
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    font-family: inherit;
}

.section-edit-btn:hover {
    background: rgba(21, 128, 61, 0.95);
    box-shadow: 0 4px 16px rgba(22, 163, 74, 0.4);
    transform: scale(1.03);
}

.section-edit-btn--active {
    background: rgba(220, 38, 38, 0.9);
    box-shadow: 0 2px 12px rgba(220, 38, 38, 0.3);
}

.section-edit-btn--active:hover {
    background: rgba(185, 28, 28, 0.95);
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.4);
}

.fab-add__icon {
    transition: transform 0.25s ease;
}

.fab-add__icon--rotated {
    transform: rotate(45deg);
}

/* FAB pop transition */
.fab-pop-enter-active,
.fab-pop-leave-active {
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.fab-pop-enter-from,
.fab-pop-leave-to {
    transform: scale(0.5);
    opacity: 0;
}

/* ── Action sheet ── */
.action-sheet-overlay {
    position: fixed;
    inset: 0;
    z-index: 43;
    background: rgba(15, 23, 42, 0.3);
}

.action-sheet {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 44;
    background: #ffffff;
    border-radius: 20px 20px 0 0;
    padding: 0 0 max(16px, env(safe-area-inset-bottom));
    box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.12);
    max-height: 75vh;
    display: flex;
    flex-direction: column;
}
.action-sheet__header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px 12px;
}
.action-sheet__scrollable,
.bgc-gradient-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

.action-sheet__title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
}

.action-sheet__close {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background: #f1f5f9;
    color: #64748b;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.12s ease;
}

.action-sheet__close:hover {
    background: #e2e8f0;
}

.action-sheet__item {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 20px;
    border: none;
    background: transparent;
    color: #0f172a;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.12s ease;
    text-align: left;
}

.action-sheet__item:hover {
    background: #f8fafc;
}

.action-sheet__item--danger {
    color: #dc2626;
}

.action-sheet__item--danger:hover {
    background: #fef2f2;
}

.action-sheet__icon {
    width: 20px;
    text-align: center;
    font-size: 18px;
    color: #64748b;
}

.action-sheet__item--danger .action-sheet__icon {
    color: #dc2626;
}

.action-sheet__color-input {
    position: absolute;
    width: 0;
    height: 0;
    padding: 0;
    border: 0;
    opacity: 0;
    pointer-events: none;
}

.action-sheet__divider {
    height: 1px;
    background: #e2e8f0;
    margin: 4px 20px;
}

/* Action sheet transitions */
.action-sheet-fade-enter-active,
.action-sheet-fade-leave-active {
    transition: opacity 0.25s ease;
}

.action-sheet-fade-enter-from,
.action-sheet-fade-leave-to {
    opacity: 0;
}

.action-sheet-slide-enter-active,
.action-sheet-slide-leave-active {
    transition: transform 0.3s ease;
}

.action-sheet-slide-enter-from,
.action-sheet-slide-leave-to {
    transform: translateY(100%);
}

/* ── Bottom edit bar ── */
.bottom-edit-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 40;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.06);
    padding: 6px 0 max(6px, env(safe-area-inset-bottom));
}

.bottom-edit-bar__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding: 0 8px;
}

@media (max-width: 768px) {
    .bottom-edit-bar__inner {
        justify-content: flex-start;
    }
}

.bottom-edit-bar__inner::-webkit-scrollbar {
    display: none;
}

/* Individual bar item */
.bbar-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-width: 52px;
    padding: 6px 6px 4px;
    border: none;
    background: transparent;
    color: #334155;
    font-size: 20px;
    cursor: pointer;
    border-radius: 8px;
    transition: background 0.15s ease, color 0.15s ease;
    position: relative;
    flex-shrink: 0;
}

.bbar-item > svg,
.bbar-item > .bbar-icon-text,
.bbar-item > .bbar-color-chip {
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bbar-item:hover {
    background: #f1f5f9;
}

.bbar-item--active {
    color: #16a34a;
    background: #f0fdf4;
}

.bbar-item--danger {
    color: #dc2626;
}

.bbar-item--danger:hover {
    color: #dc2626;
    background: #fef2f2;
}

.bbar-label {
    font-size: 10px;
    line-height: 1.2;
    margin-top: 2px;
    color: #64748b;
    white-space: nowrap;
    font-weight: 500;
}

.bbar-icon-text {
    font-size: 18px;
    font-weight: 700;
    line-height: 1;
}

.bbar-divider {
    width: 1px;
    height: 36px;
    background: #e2e8f0;
    margin: 0 4px;
    flex-shrink: 0;
    align-self: center;
}

/* ── Font picker panel ── */
.font-picker-panel {
    position: fixed;
    bottom: 60px;
    left: 0;
    right: 0;
    z-index: 39;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -4px 20px rgba(15, 23, 42, 0.08);
    max-height: 45vh;
    display: flex;
    flex-direction: column;
}

.font-picker-panel__header {
    padding: 10px 16px 6px;
    border-bottom: 1px solid #f1f5f9;
    flex-shrink: 0;
}

.font-picker-panel__title {
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.font-picker-panel__list {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
}

.font-picker-panel__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 16px;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: background 0.12s ease;
    border-bottom: 1px solid #f8fafc;
}

.font-picker-panel__item:hover {
    background: #f8fafc;
}

.font-picker-panel__item--active {
    background: #f0fdf4;
}

.font-picker-panel__font-name {
    font-size: 22px;
    color: #0f172a;
    line-height: 1.3;
}

.font-picker-panel__item--active .font-picker-panel__font-name {
    color: #16a34a;
}

.font-picker-panel__check {
    color: #16a34a;
    font-size: 16px;
    flex-shrink: 0;
    margin-left: 12px;
}

/* Font panel slide transition */
.font-panel-slide-enter-active,
.font-panel-slide-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.font-panel-slide-enter-from,
.font-panel-slide-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

/* Color chip inside bar item */
.bbar-item--color {
    cursor: pointer;
}

.bbar-color-chip {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid #e2e8f0;
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.6);
}

.bbar-color-input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
}

/* Slide-up transition */
.bottom-bar-slide-enter-active,
.bottom-bar-slide-leave-active {
    transition: transform 0.25s ease, opacity 0.25s ease;
}

.bottom-bar-slide-enter-from,
.bottom-bar-slide-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

/* ── Inline text edit overlay ── */
.inline-text-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
}

.inline-text-modal {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 90%;
    max-width: 400px;
}

.inline-text-modal__input {
    width: 100%;
    min-height: 80px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 16px;
    font-family: inherit;
    line-height: 1.4;
    outline: none;
    resize: vertical;
}

.inline-text-modal__input:focus {
    border-color: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.15);
}

.inline-text-modal__ok {
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 10px;
    background: #16a34a;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;
}

.inline-text-modal__ok:hover {
    background: #15803d;
}

/* ── Size popover ── */
.size-popover {
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.size-popover__btn {
    width: 38px;
    height: 38px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    font-size: 20px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.size-popover__btn:hover {
    background: #f1f5f9;
}

.size-popover__value {
    font-size: 18px;
    font-weight: 700;
    min-width: 36px;
    text-align: center;
    color: #0f172a;
}

/* ── Mask mode ── */
.mask-frame-layer {
    position: relative;
    z-index: 1;
    pointer-events: none;
}

.user-photo-img {
    position: absolute;
    top: 0;
    left: 0;
    min-width: 100%;
    min-height: 100%;
    width: 100%;
    height: auto;
    cursor: grab;
    user-select: none;
    -webkit-user-drag: none;
}

.user-photo-img--selected {
    outline: 2px dashed rgba(22, 163, 74, 0.7);
    outline-offset: -2px;
}

.user-photo-img:active {
    cursor: grabbing;
}

.bbar-item--value {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    min-width: 44px;
    text-align: center;
    pointer-events: none;
    user-select: none;
}

/* Photo controls two-row layout */
.photo-controls {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    padding: 4px 12px;
    box-sizing: border-box;
}

.photo-controls__sliders {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    justify-content: center;
}

.photo-controls__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
}

/* Slider group inside photo control bar */
.bbar-slider-group {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 4px;
    min-width: 0;
    flex: 1 1 auto;
}

.bbar-slider-icon {
    font-size: 14px;
    color: #64748b;
    flex-shrink: 0;
}

.bbar-slider-value {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    min-width: 42px;
    text-align: right;
    flex-shrink: 0;
    user-select: none;
}

.bbar-range {
    -webkit-appearance: none;
    appearance: none;
    width: 100px;
    height: 4px;
    background: #e2e8f0;
    border-radius: 2px;
    outline: none;
    flex: 1 1 auto;
    min-width: 60px;
}

.bbar-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #16a34a;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}

.bbar-range::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #16a34a;
    cursor: pointer;
    border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}

.photo-zone-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 3px dashed rgba(0, 0, 0, 0.25);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.15);
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
}

.photo-zone-placeholder:hover {
    background: rgba(255, 255, 255, 0.35);
    border-color: rgba(0, 0, 0, 0.4);
}

.photo-zone-placeholder__icon {
    font-size: 32px;
    color: rgba(0, 0, 0, 0.35);
}

.photo-zone-placeholder__label {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.4);
}

/* Cropper modal responsive sizing */
.crop-modal-content {
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 32px);
    overflow: auto;
    box-sizing: border-box;
}

.crop-modal-cropper {
    height: min(384px, 60vh);
    width: 100%;
}

/* ── Sticker picker panel ── */
.sticker-picker-panel {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 44;
    background: #fff;
    border-radius: 16px 16px 0 0;
    max-height: 60vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.12);
    touch-action: pan-y;
}

.sticker-picker-loading {
    padding: 32px 20px;
    text-align: center;
    color: #94a3b8;
    font-size: 14px;
}

.sticker-picker-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 0 16px 16px;
    flex: 1;
    min-height: 0;
}

.sticker-picker-category {
    margin-bottom: 12px;
}

.sticker-picker-category__name {
    font-size: 13px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
    padding: 4px 0;
    border-bottom: 1px solid #e2e8f0;
}

.sticker-picker-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.sticker-picker-item {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    background: #f8fafc;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease;
}

.sticker-picker-item:hover {
    border-color: #16a34a;
    background: #f0fdf4;
}

.sticker-picker-item img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
}

/* ── Sticker elements on canvas ── */
.sticker-element {
    cursor: grab;
}

.sticker-element--selected {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 4px;
}

.sticker-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: all;
    cursor: pointer;
    user-select: none;
}
</style>
